/* eslint-disable no-nested-ternary */
import { Flex, Box, Heading, Text, useToast, Button, Tooltip, Circle } from '@chakra-ui/core';
import React, { useCallback, useMemo, useEffect } from 'react';
import { Cell } from 'react-table';

import i18next from 'i18next';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FaRegCopy } from 'react-icons/fa';
import { IoMdVideocam } from 'react-icons/io';
import { HiUsers } from 'react-icons/hi';
import { BsCheckCircleFill } from 'react-icons/bs';
import { MdForum, MdPhone } from 'react-icons/md';
import moment from 'moment';
import { ActionEnum } from '../../../dashboard/RecruiterTodoListType';
import { FormattedTodo } from '../../types';

import BackgroundCheckIcon from '../../../../assets/img/certn-logo.svg';
import DocumentIcon from '../../../../assets/img/icon-document.svg';
import OfferIcon from '../../../../assets/img/icon-offer.svg';
import LanguageEvaluationIcon from '../../../../assets/img/icon-languageEvaluation.svg';
import ConvensionalAiIcon from '../../../../assets/img/icon-ConvensionalAi.svg';
import colors from '../../../../styles/colors';
import { getTranslatedValue } from '../../../../utils/localizedString';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import useLookupsByType from '../../../../app/hooks/useLookupsByType';
import {
  InterviewActionButton,
  InterviewHeading,
  LanguageLabels,
  TodosType,
  ToDoTitle,
} from '../../../../firebase/firestore/documents/lookups';
import useContactRequestDeclineReasons from '../../../messaging/seeker/candidate/history/useContactRequestDeclinedReasons';
import { Appointment } from '../../../../firebase/firestore/documents/candidate';
import { ReactComponent as VideocamSvg } from '../../../../assets/img/videocam.svg';
import { ReactComponent as PhoneSvg } from '../../../../assets/img/phone-flash.svg';
import { ReactComponent as PersonSvg } from '../../../../assets/img/person.svg';

import { AppointmentType } from '../../../../firebase/firestore/documents/candidateAudit';
import { getInterviewTime } from '../../../../utils/candidatesUtils';
import { isAbleToJoinInterview } from '../../../../services/CandidateService';
import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import useCandidateBooking from '../../../../app/components/useCandidateBooking';
import { SESSION_KEY_CANDIDATE_LIST_TYPE } from '../../../messaging/sidebar/filters/SeekersFiltersContext';
import { CandidateListType } from '../../../messaging/sidebar/filters/seekersFiltersContextValueType';
import { SeekerContainerTabItems } from '../../../messaging/seeker/SeekerContainerContext';
import { INTERVIEW_MESSAGING_TABS } from '../../../../routes/constants';
import { AvailableBookingWithPosition } from '../../../../firebase/firestore/documents/appointmentAvaiability';
import { useUpcomingAppointment } from '../../../../app/hooks/useUpcomingAppointment';
import { useInterviewStatus } from '../../../../app/hooks/useInterviewStatus';

interface ActionTypesProps {
  cellData: Cell<FormattedTodo, any>;
  // handleAction: (data: FormattedTodo) => void;
  // onMessagingAction: (candidateId: string) => void;
  candidateTodos: TodosType | undefined;
  declinedReasonsTypes: ReturnType<typeof useContactRequestDeclineReasons>;
  offerRejectedReasons: ReturnType<typeof useLookupsByType>;
  toDos: FormattedTodo[];
}

const iconProps = {
  size: 20,
  color: colors.blue.default,
};

const interviewIconProps = {
  size: 14,
  color: colors.white,
};

const renderIcon = (actionType: ActionEnum, appointmentData?: Appointment) => {
  switch (actionType) {
    case ActionEnum.BACKGROUND_CHECK:
      return <img src={BackgroundCheckIcon} alt="background check" height={24} width={24} />;
    case ActionEnum.OFFER:
    case ActionEnum.OFFER_REJECTED:
    case ActionEnum.OFFER_EXPIRED:
      return <img src={OfferIcon} alt="offer" height={20} width={20} />;

    case ActionEnum.OFFER_ACCEPTED:
      return <BsCheckCircleFill size={20} color={colors.green[500]} />;

    case ActionEnum.HIRE_FORM:
    case ActionEnum.I9_VERIFICATION:
      return <img src={DocumentIcon} alt="Hire form" height={20} width={20} />;
    case ActionEnum.LANGUAGE_EVALUATION:
      return <img src={LanguageEvaluationIcon} alt="Hire form" height={20} width={20} />;
    case ActionEnum.REVIEW_ANSWERS:
      return <MdForum size={20} color={colors.dodgerBlue} />;

    case ActionEnum.UPCOMING_INTERVIEWS: {
      if (!appointmentData?.type) {
        return null;
      }

      if (appointmentData?.adhoc) {
        const adhocIcons = {
          [AppointmentType.video]: <VideocamSvg />,
          [AppointmentType.phone]: <PhoneSvg />,
          [AppointmentType.inPerson]: <PersonSvg />,
        };
        return adhocIcons[appointmentData?.type];
      }

      const regularIcons = {
        [AppointmentType.phone]: <MdPhone {...iconProps} />,
        [AppointmentType.video]: <IoMdVideocam {...iconProps} />,
        [AppointmentType.inPerson]: <HiUsers {...iconProps} />,
      };

      return regularIcons[appointmentData.type];
    }

    default:
      return <img src={ConvensionalAiIcon} alt="AI" height={20} width={20} />;
  }
};

const extractDate = (raw?: string | Date | null): Date | null => {
  if (!raw) return null;
  const parsedDate = moment(raw);
  return parsedDate.isValid() ? parsedDate.toDate() : null;
};

interface ActionDescriptionProps {
  columnData: FormattedTodo;
  isRequisitionEnabled: boolean | undefined;
  candidateTodos: TodosType | undefined;
  onCopySeekerHireDetailId: (hireDetailId: string) => Promise<void>;
  declinedReasonsTypes: ReturnType<typeof useContactRequestDeclineReasons>;
  offerRejectedReasons: ReturnType<typeof useLookupsByType>;
  appointmentData: Appointment | undefined;
}

const ActionDescription: React.FC<ActionDescriptionProps> = React.memo(function ActionDescription({
  columnData,
  isRequisitionEnabled,
  candidateTodos,
  onCopySeekerHireDetailId,
  declinedReasonsTypes,
  offerRejectedReasons,
  appointmentData,
}) {
  const { t, i18n } = useTranslation();
  const { title, seekerHireDetailId, offerAcceptanceStatus, category } = columnData;

  const detailObject =
    !isRequisitionEnabled && title === ToDoTitle.HR_FORM_COMPLETED_24HOURS
      ? candidateTodos?.[title as keyof TodosType]?.general?.details
      : candidateTodos?.[title as keyof TodosType]?.details;
  const description = detailObject ? getTranslatedValue(detailObject, i18next.language) : title;

  const revokeReason = useCallback(
    (reasonValue: string | undefined) => {
      let reasonText;
      reasonText = declinedReasonsTypes.find((reason) => reason?.reasonType === reasonValue)?.text;
      if (isEmpty(reasonText)) {
        reasonText = offerRejectedReasons.find((reason) => reason.id === reasonValue)?.label;
      }
      return reasonText && getTranslatedValue(reasonText, i18n.language);
    },
    [declinedReasonsTypes, offerRejectedReasons, i18n.language],
  );

  return (
    <Box>
      <Text noOfLines={2} isTruncated fontSize="sm">
        {description}
        {appointmentData && t('toDoList:interviewTime', { time: `${getInterviewTime(appointmentData.fromDate)}` })}
      </Text>

      {appointmentData?.collaborators && appointmentData?.collaborators?.length > 0 && (
        <Flex alignItems="flex-start">
          <Text fontSize="12px" color={colors.blue[500]} wordBreak="break-word" noOfLines={2} isTruncated>
            <Text as="span" fontSize="14px" color={colors.blue[500]} fontWeight="medium" mr={1}>
              {t('toDoList:coInterviewer')}
            </Text>

            {appointmentData.collaborators.slice(0, 2).join(', ')}

            {appointmentData.collaborators.length > 2 && (
              <Tooltip hasArrow placement="top" fontSize="12px" label={appointmentData.collaborators.slice(2).join(', ')}>
                <Text as="span" ml={2} color={colors.blue[600]} cursor="pointer" textDecoration="underline">
                  {t('toDoList:collaboratorCount', { count: appointmentData.collaborators.length - 2 })}
                </Text>
              </Tooltip>
            )}
          </Text>
        </Flex>
      )}

      {seekerHireDetailId && (
        <Flex d="inline-flex" alignItems="center">
          <Flex mr={2}>
            <Text whiteSpace="nowrap" mr={1}>
              {t('toDoList:hireFormId')}
            </Text>
            <Text noOfLines={1} isTruncated maxWidth={200}>
              {seekerHireDetailId}
            </Text>
          </Flex>
          <FaRegCopy
            size={16}
            onClick={() => onCopySeekerHireDetailId(seekerHireDetailId)}
            data-testid={`copy-${seekerHireDetailId}`}
          />
        </Flex>
      )}

      {offerAcceptanceStatus && (category === 'offerRejected' || category === 'offerExpired') && (
        <Flex display="inline-flex">
          <Text mr={1}>{t('toDoList:reason')}</Text>
          <Text fontSize="sm" color="gray.500" fontWeight="bold">
            {offerAcceptanceStatus.reason === 'OTHER'
              ? `${revokeReason(offerAcceptanceStatus.reason)} - ${offerAcceptanceStatus.customReason}`
              : revokeReason(offerAcceptanceStatus.reason)}
          </Text>
        </Flex>
      )}
    </Box>
  );
});

interface ActionButtonProps {
  columnData: FormattedTodo;
  isRequisitionEnabled: boolean | undefined;
  candidateTodos: TodosType | undefined;
  canJoinInterview: boolean;
  appointmentData: Appointment | undefined;
}

const ActionButton: React.FC<ActionButtonProps> = React.memo(function ActionButton({
  columnData,
  isRequisitionEnabled,
  candidateTodos,
  canJoinInterview,
  appointmentData,
}) {
  const history = useHistory();
  const { title, candidateId } = columnData;
  const candidateAndBookingDetails = useCandidateBooking(candidateId);

  const setInterviewEvent = useStoreActions((state) => state.interview.setInterviewEvent);
  const { setSelectedTodo, setMessagingViewModal } = useStoreActions((actions) => actions.todo);

  const isJoinableVideoInterview = canJoinInterview && appointmentData?.type === AppointmentType.video;

  const hasButton = candidateTodos?.[title as keyof TodosType];

  let actionLabel: LanguageLabels | undefined;

  if (title === ToDoTitle.UPCOMING_INTERVIEWS) {
    const interviewLabels = hasButton?.actionButton as InterviewActionButton;
    actionLabel = isJoinableVideoInterview ? interviewLabels?.joinInterview : interviewLabels?.candidateDetails;
  } else {
    actionLabel =
      !isRequisitionEnabled && hasButton?.general?.actionButton
        ? hasButton.general.actionButton
        : (hasButton?.actionButton as LanguageLabels);
  }

  const onJoinInterview = () => {
    setInterviewEvent(candidateAndBookingDetails as AvailableBookingWithPosition);

    sessionStorage.setItem(SESSION_KEY_CANDIDATE_LIST_TYPE, CandidateListType.UNKNOWN);
    history.push(INTERVIEW_MESSAGING_TABS(candidateId, SeekerContainerTabItems.Messaging), {
      redirectedFromPage: 'dashboard',
    });
  };

  if (!actionLabel) return <Box />;

  return (
    <Button
      onClick={() => {
        if (isJoinableVideoInterview) {
          onJoinInterview();
        } else {
          setSelectedTodo(columnData);
          setMessagingViewModal(true);
        }
      }}
      colorScheme={canJoinInterview ? 'orange' : 'blue'}
      w="170px"
      fontSize="14px"
      fontWeight="600"
      borderRadius="40px"
      data-testid={`ActionButton-${candidateId}-${title}`}
      flexShrink={0}
    >
      {getTranslatedValue(actionLabel, i18next.language)}
    </Button>
  );
});

export const ActionTypes = React.memo(function ActionTypes({
  cellData,
  candidateTodos,
  declinedReasonsTypes,
  offerRejectedReasons,
  toDos,
}: ActionTypesProps) {
  const { t } = useTranslation('toDoList');
  const toast = useToast();
  const isRequisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);
  const accountId = useStoreState((state) => state.app.accounts?.id);
  const counts = useStoreState((state) => state.todo.counts);
  const interviewCounts = useStoreState((state) => state.todo.interviewCounts);

  const hasHandledCancellation = React.useRef(false);

  const { fetchUpcomingAppointments, removeTodoByCandidateOrDocId } = useStoreActions((actions) => actions.todo);

  const gracePeriod: number = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.JOIN_VIDEO_GRACE_PERIOD_IN_MINUTE];
  });

  const [currentTime, setCurrentTime] = React.useState(() => moment());

  const columnData = cellData.row.original;
  const { candidateId } = columnData;

  const candidateAppointment = useUpcomingAppointment(candidateId);

  const originalAppointment = columnData?.appointment;
  const latestAppointment = candidateAppointment?.appointment;

  const hasAppointmentDataChanged = useMemo(() => {
    if (!latestAppointment || !originalAppointment) {
      return false;
    }

    return !isEqual(originalAppointment, latestAppointment);
  }, [originalAppointment, latestAppointment]);

  const appointment = hasAppointmentDataChanged ? latestAppointment : originalAppointment;

  const fromDate = useMemo(() => extractDate(appointment?.fromDate), [appointment]);
  const toDate = useMemo(() => extractDate(appointment?.toDate), [appointment]);

  const interviewStatus = useInterviewStatus({ fromDate, toDate });
  const interviewType = appointment?.type;

  const newAppointment = interviewStatus === 'completed' ? originalAppointment : appointment;
  const statusPrefix = interviewStatus === 'ongoing' ? t('isOngoing') : interviewStatus === 'upcoming' ? t('isUpcoming') : '';

  // track and fetch to appointment status changes
  useEffect(() => {
    if (appointment?.status === 'cancelled' && !hasHandledCancellation.current) {
      hasHandledCancellation.current = true;

      // Instantly remove from local store
      removeTodoByCandidateOrDocId({
        candidateId,
        docId: columnData.id,
      });

      // Refetch upcoming appointments to keep local store in sync with the server data
      if (accountId && counts && interviewCounts) {
        fetchUpcomingAppointments({
          accountId,
          todoCount: interviewCounts?.toDo ?? counts?.toDo,
          interviewCount: interviewCounts?.upcomingInterviews,
          total: interviewCounts?.total ?? counts?.total,
        }).catch((error) => {
          console.error('Failed to refetch upcoming appointments:', error);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    candidateId,
    appointment,
    columnData.id,
    counts,
    interviewCounts,
    fetchUpcomingAppointments,
    removeTodoByCandidateOrDocId,
  ]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentTime(moment());
    }, 60 * 1000);

    return () => window.clearInterval(interval);
  }, []);

  // Collect all IDs for joinable interviews active in the current window
  const joinableInterviewIds = useMemo(
    () =>
      new Set(
        toDos
          .filter(
            (todo) =>
              todo.title === ToDoTitle.UPCOMING_INTERVIEWS &&
              todo.appointment &&
              isAbleToJoinInterview(todo.appointment, gracePeriod, currentTime),
          )
          .map((todo) => todo.id),
      ),
    [toDos, gracePeriod, currentTime],
  );

  const isJoinableInterview = columnData.title === ToDoTitle.UPCOMING_INTERVIEWS && joinableInterviewIds.has(columnData.id);

  const getColumnTitle = (title: string) => {
    const heading =
      !isRequisitionEnabled && title === ToDoTitle.HR_FORM_COMPLETED_24HOURS
        ? candidateTodos?.[title as keyof TodosType]?.general?.heading
        : candidateTodos?.[title as keyof TodosType]?.heading;

    if (!heading) return title;

    if (title === ToDoTitle.UPCOMING_INTERVIEWS) {
      const interviewHeading = heading as InterviewHeading;

      const resolvedHeading =
        interviewType === AppointmentType.video
          ? interviewHeading.videoInterview
          : interviewType === AppointmentType.phone
          ? interviewHeading.phoneInterview
          : interviewHeading.inpersonInterview;

      return getTranslatedValue(resolvedHeading, i18next.language);
    }

    return getTranslatedValue(heading as LanguageLabels, i18next.language);
  };

  const onCopySeekerHireDetailId = useCallback(
    async (hireDetailId: string) => {
      await navigator.clipboard.writeText(hireDetailId);
      toast({
        title: t('toDoList:hireDetailIdCopiedInfo'),
        duration: 2000,
        isClosable: true,
      });
    },
    [toast, t],
  );

  return (
    <Flex
      className={cellData.column.id}
      flex={1}
      data-testid={cellData.column.id}
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex mr="15px" flex={1}>
        <Box mr={2} h="18px" w="18px" minW="24px">
          {isJoinableInterview ? (
            <Circle size="24px" bg="orange.500" flexShrink={0}>
              {interviewType === AppointmentType.video ? (
                <IoMdVideocam {...interviewIconProps} />
              ) : interviewType === AppointmentType.phone ? (
                <MdPhone {...interviewIconProps} />
              ) : (
                <HiUsers {...interviewIconProps} />
              )}
            </Circle>
          ) : (
            renderIcon(cellData.value as ActionEnum, appointment)
          )}
        </Box>
        <Box>
          <Heading fontSize="16px" color="gray.900" mb="5px" lineHeight="1.2" isTruncated noOfLines={1}>
            {statusPrefix}
            {getColumnTitle(columnData.title)}
            {newAppointment && t('interviewTime', { time: `${getInterviewTime(newAppointment.fromDate)}` })}
          </Heading>
          <Text as="div" fontSize="15px" color="gray.700">
            <ActionDescription
              columnData={columnData}
              isRequisitionEnabled={isRequisitionEnabled}
              candidateTodos={candidateTodos}
              declinedReasonsTypes={declinedReasonsTypes}
              offerRejectedReasons={offerRejectedReasons}
              onCopySeekerHireDetailId={onCopySeekerHireDetailId}
              appointmentData={newAppointment}
            />
          </Text>
        </Box>
      </Flex>
      <ActionButton
        columnData={columnData}
        isRequisitionEnabled={isRequisitionEnabled}
        candidateTodos={candidateTodos}
        canJoinInterview={isJoinableInterview}
        appointmentData={newAppointment}
      />
    </Flex>
  );
});
